import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EnhancedHeader } from "@/components/EnhancedHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { 
  TrendingUp, 
  TrendingDown, 
  Activity, 
  Target, 
  Trophy, 
  ArrowLeft,
  Calendar,
  DollarSign,
  Percent,
  Flame
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { StructuredData } from "@/components/StructuredData";

interface DashboardStats {
  total_bets: number;
  total_won: number;
  total_lost: number;
  total_pending: number;
  total_staked: number;
  total_returns: number;
  roi_percentage: number;
  win_rate: number;
  current_streak: number;
  best_winning_streak: number;
  best_losing_streak: number;
}

interface BetHistory {
  id: string;
  sport: string;
  bet_amount: number;
  odds: number;
  potential_return: number;
  outcome: string;
  placed_at: string;
  settled_at: string | null;
  notes: string | null;
}

interface SportBreakdown {
  sport: string;
  total_bets: number;
  won: number;
  lost: number;
  staked: number;
  returns: number;
  win_rate: string;
  roi: string;
}

export default function UserDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentBets, setRecentBets] = useState<BetHistory[]>([]);
  const [sportBreakdown, setSportBreakdown] = useState<SportBreakdown[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "My Dashboard – BetVerse Performance Tracker";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 
        'Track your betting performance with detailed analytics, ROI calculations, win rates, and streak tracking. Monitor your betting history and improve your strategy.'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://betverse-global-picks.lovable.app/dashboard');
    }
  }, []);

  useEffect(() => {
    if (!user) {
      toast.error("Please sign in to view your dashboard");
      navigate("/auth");
      return;
    }

    fetchDashboardData();
  }, [user, navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('get-user-dashboard', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });

      if (error) throw error;

      if (data.success) {
        setStats(data.data.stats);
        setRecentBets(data.data.recent_bets);
        setSportBreakdown(data.data.sport_breakdown);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const dashboardSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "User Dashboard – BetVerse",
    "description": "Personal betting performance tracking dashboard with detailed analytics and ROI calculations",
    "url": "https://betverse-global-picks.lovable.app/dashboard"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <EnhancedHeader />
        <div className="container py-12">
          <div className="text-center">Loading your dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <StructuredData type="WebPage" data={dashboardSchema} />
      <EnhancedHeader />

      {/* Hero Section */}
      <section className="py-8 border-b bg-gradient-to-b from-primary/5 to-background">
        <div className="container">
          <Button variant="ghost" size="sm" asChild className="mb-4">
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">My Dashboard</h1>
              <p className="text-muted-foreground">
                Track your betting performance and analyze your strategy
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="h-8 w-8 text-primary" />
            </div>
          </div>
        </div>
      </section>

      {/* Key Stats */}
      <section className="py-8">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Bets */}
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Bets</p>
                  <p className="text-3xl font-bold">{stats?.total_bets || 0}</p>
                </div>
                <Target className="h-8 w-8 text-primary" />
              </div>
            </Card>

            {/* Win Rate */}
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Win Rate</p>
                  <p className="text-3xl font-bold">{stats?.win_rate?.toFixed(1) || 0}%</p>
                </div>
                <Trophy className="h-8 w-8 text-primary" />
              </div>
            </Card>

            {/* ROI */}
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">ROI</p>
                  <p className={`text-3xl font-bold ${
                    (stats?.roi_percentage || 0) >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {stats?.roi_percentage >= 0 ? '+' : ''}{stats?.roi_percentage?.toFixed(1) || 0}%
                  </p>
                </div>
                {(stats?.roi_percentage || 0) >= 0 ? (
                  <TrendingUp className="h-8 w-8 text-green-500" />
                ) : (
                  <TrendingDown className="h-8 w-8 text-red-500" />
                )}
              </div>
            </Card>

            {/* Current Streak */}
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Current Streak</p>
                  <p className={`text-3xl font-bold ${
                    (stats?.current_streak || 0) >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {stats?.current_streak >= 0 ? '+' : ''}{stats?.current_streak || 0}
                  </p>
                </div>
                <Flame className="h-8 w-8 text-orange-500" />
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Financial Overview */}
      <section className="py-8 bg-muted/30">
        <div className="container">
          <h2 className="text-2xl font-bold mb-6">Financial Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="h-5 w-5 text-primary" />
                <p className="text-sm font-medium text-muted-foreground">Total Staked</p>
              </div>
              <p className="text-2xl font-bold">${stats?.total_staked?.toFixed(2) || '0.00'}</p>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="h-5 w-5 text-green-500" />
                <p className="text-sm font-medium text-muted-foreground">Total Returns</p>
              </div>
              <p className="text-2xl font-bold text-green-500">
                ${stats?.total_returns?.toFixed(2) || '0.00'}
              </p>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <Percent className="h-5 w-5 text-primary" />
                <p className="text-sm font-medium text-muted-foreground">Net Profit/Loss</p>
              </div>
              <p className={`text-2xl font-bold ${
                ((stats?.total_returns || 0) - (stats?.total_staked || 0)) >= 0 
                  ? 'text-green-500' 
                  : 'text-red-500'
              }`}>
                ${((stats?.total_returns || 0) - (stats?.total_staked || 0)).toFixed(2)}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Sport Breakdown */}
      {sportBreakdown.length > 0 && (
        <section className="py-8">
          <div className="container">
            <h2 className="text-2xl font-bold mb-6">Performance by Sport</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sportBreakdown.map((sport) => (
                <Card key={sport.sport} className="p-6">
                  <h3 className="text-lg font-semibold mb-4 capitalize">{sport.sport}</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Bets:</span>
                      <span className="font-medium">{sport.total_bets}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Win Rate:</span>
                      <span className="font-medium">{sport.win_rate}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">ROI:</span>
                      <span className={`font-medium ${
                        parseFloat(sport.roi) >= 0 ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {parseFloat(sport.roi) >= 0 ? '+' : ''}{sport.roi}%
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recent Bets */}
      <section className="py-8 bg-muted/30">
        <div className="container">
          <h2 className="text-2xl font-bold mb-6">Recent Bets</h2>
          {recentBets.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground mb-4">No bets tracked yet. Start tracking your bets to see your performance!</p>
              <Button asChild>
                <Link to="/tips">Browse Tips</Link>
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {recentBets.map((bet) => (
                <Card key={bet.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold capitalize">{bet.sport}</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          bet.outcome === 'won' 
                            ? 'bg-green-500/10 text-green-500'
                            : bet.outcome === 'lost'
                            ? 'bg-red-500/10 text-red-500'
                            : 'bg-yellow-500/10 text-yellow-500'
                        }`}>
                          {bet.outcome}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>Stake: ${bet.bet_amount}</span>
                        <span>Odds: {bet.odds}</span>
                        <span>Potential: ${bet.potential_return.toFixed(2)}</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(bet.placed_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Streak Info */}
      {stats && (stats.best_winning_streak > 0 || stats.best_losing_streak > 0) && (
        <section className="py-8">
          <div className="container">
            <h2 className="text-2xl font-bold mb-6">Streak Records</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Trophy className="h-6 w-6 text-green-500" />
                  <p className="text-lg font-semibold">Best Winning Streak</p>
                </div>
                <p className="text-4xl font-bold text-green-500">
                  {stats.best_winning_streak} wins
                </p>
              </Card>

              <Card className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingDown className="h-6 w-6 text-red-500" />
                  <p className="text-lg font-semibold">Longest Losing Streak</p>
                </div>
                <p className="text-4xl font-bold text-red-500">
                  {stats.best_losing_streak} losses
                </p>
              </Card>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
